<script>
import StaffDashboardTableRow from '@/views/dashboards/widgets/StaffDashboardTableRow.vue';
import {mapGetters} from 'vuex';

export default {
   name: 'StaffDashboardTable',
   props: {
      companies: {
         type: Array,
         default: function () {
            return [];
         },
      },
      isFiltered: {
         type: Boolean,
         default: false,
      },
      hasCompanies: {
         type: Boolean,
         default: false,
      },
   },
   components: {StaffDashboardTableRow},

   methods: {
      sortRows(columnName) {
         let newOrder = 'ascending';
         if (columnName === this.sortBy.columnName) {
            if (this.sortBy.order === 'ascending') {
               newOrder = 'descending';
            }
         }
         this.sortBy = {columnName, order: newOrder};
      },
   },

   watch: {
      sortBy(newValue) {
         this.$emit('onSortChange', newValue);
      },
   },

   computed: {
      ...mapGetters(['isExternalPartner']),
   },

   data() {
      return {
         sortBy: {
            columnName: 'clientName',
            order: 'ascending',
         },
      };
   },
};
</script>

<template>
   <div
      role="table"
      aria-label="Staff Dashboard"
      :aria-rowcount="companies.length"
      style="width: 100%"
   >
      <div class="border-top border-bottom" role="rowgroup">
         <div role="row" class="d-flex justify-content-between align-items-baseline">
            <div
               style="max-width: 16.25rem; line-height: 40px; cursor: pointer"
               role="columnheader"
               :aria-sort="sortBy.columnName === 'clientName' ? sortBy.order : 'none'"
               class="col"
               @click="sortRows('clientName')"
            >
               Client Name
               <b-icon-sort-up
                  v-if="sortBy.columnName === 'clientName' && sortBy.order === 'ascending'"
               ></b-icon-sort-up>
               <b-icon-sort-down
                  v-if="sortBy.columnName === 'clientName' && sortBy.order === 'descending'"
               ></b-icon-sort-down>
            </div>
            <div role="columnheader" aria-sort="none" class="col">Stage</div>
            <div
               style="max-width: 145px; line-height: 40px; cursor: pointer"
               role="columnheader"
               :aria-sort="sortBy.columnName === 'filingDeadline' ? sortBy.order : 'none'"
               class="col"
               @click="sortRows('filingDeadline')"
            >
               Filing Deadline
               <b-icon-sort-up
                  v-if="sortBy.columnName === 'filingDeadline' && sortBy.order === 'ascending'"
               ></b-icon-sort-up>
               <b-icon-sort-down
                  v-if="sortBy.columnName === 'filingDeadline' && sortBy.order === 'descending'"
               ></b-icon-sort-down>
            </div>
            <div
               style="max-width: 50px; line-height: 40px; cursor: pointer"
               role="columnheader"
               aria-sort="none"
               class="col"
            ></div>
         </div>
      </div>
      <div role="rowgroup" v-if="companies.length !== 0">
         <div v-for="company in companies" :key="company.id">
            <StaffDashboardTableRow
               :company-name="company.companyName"
               :filing-deadline-month="company.filingDeadlineMonth"
               :filing-deadline-day="company.filingDeadlineDay"
               :payroll-tax-election="company.payrollTaxElection"
               :id="company.id"
               :studies="company.openStudies"
               :business-managers="company.businessManagers"
               :project-managers="company.projectManagers"
               :project-associates="company.projectAssociates"
               :study-team-members="company.studyTeamMembers"
            ></StaffDashboardTableRow>
         </div>
      </div>
      <div role="rowgroup" class="table__empty p-5 border-bottom" v-if="companies.length === 0">
         <div class="mx-auto" v-if="isFiltered && hasCompanies">
            <h6 class="font-weight-normal mb-3">No companies in the selected stages</h6>

            <div class="text-dark mb-0">
               <p>Try adjusting or clearing the filters to see your client companies.</p>
            </div>
         </div>
         <div class="mx-auto" v-else>
            <h6 class="font-weight-normal mb-3">You Don't Have Any Client Companies</h6>

            <div class="text-dark mb-5">
               <p v-if="!isExternalPartner">
                  If you have a role with a client, ask someone on the Client Team to assign you to
                  that company as a Staff member. Click <b>Add New Company</b> to add a client to
                  Onboard.
               </p>
               <p v-else>
                  If you have a role with a client, ask someone on the Client Team to assign you to
                  that company.
               </p>
            </div>
            <div v-if="!isExternalPartner">
               <b-button variant="white" size="sm" :to="{name: 'company-create'}">
                  Add New Company
               </b-button>
            </div>
         </div>
      </div>
   </div>
</template>

<style scoped lang="scss">
.is-open {
   background-color: #f9fbfe;
}

.col {
   font-size: 0.75rem !important;
}

.table {
   &__empty {
      background-color: $primary-100;
      text-align: center;
   }
}
</style>
